import { Divider, Group } from '@byecode/ui'
import { CUSTOM_VIEW_LAYOUT } from '@lighthouse/core'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { AutoLayoutSetting } from '@/components/StyleSetting/AutoLayoutSetting'
import { DesignSetting } from '@/components/StyleSetting/DesignSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'

import { dataSourceAtom } from '../atoms'
import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { CustomStatisticSetting } from './Basic/CustomStatisticSetting'
import { DataSetting } from './Basic/DataSetting'
import { DefaultDataSource } from './Basic/DataSource'
import { CalendarDateSelector } from './Basic/DataSource/CalendarDateSelector'
import { CommentFieldMap } from './Basic/DataSource/CommentFieldMap'
import { MultiDimensionalFieldSelector } from './Basic/DataSource/MultiDimensionalFieldSelector'
import { TreeFieldSelector } from './Basic/DataSource/TreeFieldSelector'
import { EmptySetting } from './Basic/EmptySetting'
import { ExpandReplies } from './Basic/ExpandReplies'
import { StyleSwitcher } from './Basic/StyleSwitcher'
import { View } from './Basic/View'
import { CarouselSetting } from './Basic/View/CarouselSetting'
import { MultiDimensionalSetting } from './Basic/View/MultiDimensionalSetting'
import { TreeSetting } from './Basic/View/TreeSetting'
import { ViewStyles } from './Basic/View/ViewStyles'
import { Operate } from './Operate'

interface CustomViewSettingProps {
    id: string
}

export const CustomViewSetting: React.FC<CustomViewSettingProps> = ({ id }) => {
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const dataSource = useAtomValue(dataSourceAtom)
    const styleProps = getBlockAndPageDesignLimit('customView')

    const { watch } = useFormContext()
    const [layout] = watch(['breakPoint.viewLayout.layout'])
    const isCalendarLayout = layout === CUSTOM_VIEW_LAYOUT.weekCalendar || layout === CUSTOM_VIEW_LAYOUT.monthCalendar
    const isCarousel = layout === CUSTOM_VIEW_LAYOUT.carousel
    const isComment = layout === CUSTOM_VIEW_LAYOUT.comment
    const isTree = layout === CUSTOM_VIEW_LAYOUT.tree
    const isMultiDimensional = layout === CUSTOM_VIEW_LAYOUT.multiDimensional

    return (
        <>
            <BlockConfigureSegmentedControl isInterAction value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <StyleSwitcher />
                    {isCarousel && (
                        <>
                            <Divider />
                            <CarouselSetting />
                        </>
                    )}
                    {isCalendarLayout && (
                        <>
                            <Divider />
                            <ViewStyles customViewDsId={dataSource?.id} />
                        </>
                    )}
                    {isComment && (
                        <>
                            <Divider />
                            <ExpandReplies />
                        </>
                    )}
                    {isTree && (
                        <>
                            <Divider />
                            <TreeSetting />
                        </>
                    )}
                    {isMultiDimensional && (
                        <>
                            <Divider />
                            <MultiDimensionalSetting customViewDsId={dataSource?.id} />
                        </>
                    )}
                    <Divider />
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <View />
                    <Divider />
                    <AutoLayoutSetting label="卡片布局" {...styleProps.layout} disablePercent />
                    <Divider />
                    <Group label="卡片样式">
                        <DesignSetting {...styleProps.design} customViewDsId={dataSource?.id} />
                    </Group>
                    <Divider />
                    <EmptySetting />
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <DefaultDataSource enableAggregate />
                    {dataSource && (
                        <>
                            {isCalendarLayout && (
                                <>
                                    <Divider />
                                    <CalendarDateSelector dataSource={dataSource} layout={layout} />
                                </>
                            )}
                            {isComment && (
                                <>
                                    <Divider />
                                    <CommentFieldMap dataSource={dataSource} />
                                </>
                            )}
                            {isTree && (
                                <>
                                    <Divider />
                                    <TreeFieldSelector dataSource={dataSource} />
                                </>
                            )}
                            {isMultiDimensional && (
                                <>
                                    <Divider />
                                    <MultiDimensionalFieldSelector dataSource={dataSource} />
                                </>
                            )}
                            <Divider />
                            <DataSetting dataSource={dataSource} />
                            <Divider />
                            <CustomStatisticSetting dataSource={dataSource} />
                            <Divider />
                            <AnchorSetting />
                            <Divider />
                            <VisibilitySetting />
                        </>
                    )}
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && dataSource && (
                <>
                    <Operate id={id} dataSource={dataSource} />
                    <Divider />
                    <AnimationSetting />
                </>
            )}
        </>
    )
}
